import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { fundService } from '@/services/fundService';
import { DEFAULT_LEDGER_VIEW, type LedgerView } from '@/types/drawdown';
import {
  Fund,
  FundFigure,
  FundMetrics,
  FundMetricsSeries,
  FundSummary,
  FundOption,
  FundCreate,
  FundUpdate,
  FundBankAccount,
  FundEntity,
  EmailInvestorsRequest,
  EmailPreviewRequest,
  EmailPreviewResponse,
  FundEmailBatchSummary,
  FundPortalPublication,
  FundPortalPublicationUpdate,
  MetricsReleaseQueueItem,
  MetricsReleaseAction,
} from '@/types/fund';
import { InvestorDetail } from '@/types/investor';
import {
  CapTableFinality,
  ConvertibleInstrumentFundDetail,
} from '@/types/captable';
import { queryKeys } from './queryKeys';
import {
  keepPreviousData,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { APIError } from '@/utils/api-client';
import {
  useFundInvestors as useInvestorsFundInvestors,
  useFundInvestorStatistics as useInvestorsFundStats,
} from './useInvestors';
import { usePortfolioCompanies as usePortfolioCompaniesHook } from './usePortfolio';

// Query hooks
export const useFunds = (params?: {
  skip?: number;
  limit?: number;
  fund_type?: string;
  status?: string;
  include_archived?: boolean;
}) => {
  return useApiQuery<FundSummary[]>(
    queryKeys.funds.list(params),
    () => fundService.getFunds(params),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useFundStatistics = () => {
  return useApiQuery(
    queryKeys.funds.statistics(),
    () => fundService.getFundStatistics(),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useFundOptions = (params?: { include_archived?: boolean }) => {
  return useApiQuery<FundOption[]>(
    queryKeys.funds.options(params),
    () => fundService.getFundOptions(params),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes - these don't change often
    }
  );
};

export const useFund = (fundId: string | undefined) => {
  return useApiQuery<Fund>(
    queryKeys.funds.detail(fundId!),
    () => fundService.getFund(fundId!),
    {
      enabled: !!fundId,
    }
  );
};

export const useFundMetrics = (
  fundId: string,
  view: LedgerView = DEFAULT_LEDGER_VIEW,
  asOf?: string
) => {
  return useApiQuery<FundMetrics>(
    queryKeys.metrics.fund(fundId, view, asOf),
    () => fundService.getFundMetrics(fundId, view, asOf),
    {
      enabled: !!fundId,
      placeholderData: keepPreviousData,
    }
  );
};

export const useFundMetricsSeries = (
  fundId: string,
  view: LedgerView = DEFAULT_LEDGER_VIEW,
  from?: string,
  to?: string
) => {
  return useApiQuery<FundMetricsSeries>(
    queryKeys.metrics.fundSeries(fundId, view, from, to),
    () => fundService.getFundMetricsSeries(fundId, view, from, to),
    {
      enabled: !!fundId,
      placeholderData: keepPreviousData,
    }
  );
};

export const useFundFigures = () => {
  return useApiQuery<FundFigure[]>(
    queryKeys.metrics.fundFigures(),
    () => fundService.getFundFigures(),
    { staleTime: Number.POSITIVE_INFINITY }
  );
};

export const useFundBankAccounts = (
  fundId: string | undefined,
  activeOnly?: boolean
) => {
  return useApiQuery<FundBankAccount[]>(
    queryKeys.funds.bankAccounts(fundId!, activeOnly),
    () => fundService.getFundBankAccounts(fundId!, activeOnly),
    {
      enabled: !!fundId,
    }
  );
};

export const useFundInvestors = (fundId: string | undefined) => {
  return useApiQuery<InvestorDetail[]>(
    queryKeys.funds.investors(fundId!),
    () => fundService.getFundInvestors(fundId!),
    {
      enabled: !!fundId,
    }
  );
};

export const useFundConvertibles = (
  fundId: string | undefined,
  outstandingOnly: boolean = true
) => {
  return useApiQuery<ConvertibleInstrumentFundDetail[]>(
    queryKeys.funds.convertibles(fundId!, outstandingOnly),
    () => fundService.getFundConvertibles(fundId!, outstandingOnly),
    {
      enabled: !!fundId,
    }
  );
};

export const useFundPortalPublication = (fundId: string | undefined) => {
  return useApiQuery<FundPortalPublication>(
    queryKeys.funds.portalPublication(fundId!),
    () => fundService.getFundPortalPublication(fundId!),
    {
      enabled: !!fundId,
    }
  );
};

export const useUpdateFundPortalPublication = () => {
  return useApiMutation<
    FundPortalPublication,
    APIError,
    { fundId: string; data: FundPortalPublicationUpdate }
  >(
    ({ fundId, data }) => fundService.updateFundPortalPublication(fundId, data),
    {
      mutationKey: ['updateFundPortalPublication'],
      invalidateKeys: (_data, variables) => [
        queryKeys.funds.portalPublication(variables.fundId),
      ],
      successMessage: 'Portal visibility updated successfully',
    }
  );
};

// ── Fund-metrics period release ──────────────────────────────────────────

export const useMetricsReleaseQueue = () => {
  return useApiQuery<MetricsReleaseQueueItem[]>(
    queryKeys.funds.metricsReleaseQueue(),
    () => fundService.getMetricsReleaseQueue()
  );
};

export const useConfirmMetricsRelease = () => {
  return useApiMutation<
    FundPortalPublication[],
    APIError,
    MetricsReleaseAction
  >((data) => fundService.confirmMetricsRelease(data), {
    mutationKey: ['confirmMetricsRelease'],
    invalidateKeys: () => [queryKeys.funds.metricsReleaseQueue()],
    successMessage: 'Metrics released',
  });
};

export const useRevokeMetricsRelease = () => {
  return useApiMutation<
    FundPortalPublication[],
    APIError,
    MetricsReleaseAction
  >((data) => fundService.revokeMetricsRelease(data), {
    mutationKey: ['revokeMetricsRelease'],
    invalidateKeys: () => [queryKeys.funds.metricsReleaseQueue()],
    successMessage: 'Metrics visibility revoked',
  });
};

export const useFundEmails = (fundId: string) => {
  return useApiQuery<FundEmailBatchSummary[]>(
    queryKeys.funds.emails(fundId),
    () => fundService.getFundEmails(fundId),
    {
      enabled: !!fundId,
    }
  );
};

// Mutation hooks
export const useCreateFund = () => {
  return useApiMutation<Fund, APIError, FundCreate>(
    (fundData) => fundService.createFund(fundData),
    {
      mutationKey: ['createFund'],
      invalidateKeys: [queryKeys.funds.all],
      successMessage: 'Fund created successfully',
    }
  );
};

export const useUpdateFund = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    Fund,
    APIError,
    { id: string; data: FundUpdate },
    { previousFund?: Fund }
  >(({ id, data }) => fundService.updateFund(id, data), {
    mutationKey: ['updateFund'],
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.funds.detail(id),
      });

      // Snapshot the previous value
      const previousFund = queryClient.getQueryData<Fund>(
        queryKeys.funds.detail(id)
      );

      // Optimistically update to the new value
      if (previousFund) {
        queryClient.setQueryData(queryKeys.funds.detail(id), {
          ...previousFund,
          ...data,
        });
      }

      // Return a context object with the snapshotted value
      return { previousFund };
    },
    onError: (_err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to rollback
      if (context?.previousFund) {
        queryClient.setQueryData(
          queryKeys.funds.detail(variables.id),
          context.previousFund
        );
      }
    },
    invalidateKeys: (_data, variables) => [
      queryKeys.funds.all,
      queryKeys.funds.detail(variables.id),
      // Leaving deal-by-deal resets draft custom splits.
      queryKeys.fundTransactions.lpAllocationsAll(variables.id),
    ],
    successMessage: 'Fund updated successfully',
  });
};

export const useCreateBankAccount = () => {
  return useApiMutation<
    FundBankAccount,
    APIError,
    {
      fundId: string;
      data: Omit<
        FundBankAccount,
        'id' | 'fund_id' | 'created_at' | 'updated_at'
      >;
    }
  >(({ fundId, data }) => fundService.createBankAccount(fundId, data), {
    invalidateKeys: (_data, variables) => [
      queryKeys.funds.bankAccounts(variables.fundId),
      queryKeys.funds.detail(variables.fundId),
    ],
    successMessage: 'Bank account created successfully',
  });
};

export const useUpdateBankAccount = () => {
  return useApiMutation<
    FundBankAccount,
    APIError,
    {
      fundId: string;
      accountId: string;
      data: Partial<
        Omit<FundBankAccount, 'id' | 'fund_id' | 'created_at' | 'updated_at'>
      >;
    }
  >(
    ({ fundId, accountId, data }) =>
      fundService.updateBankAccount(fundId, accountId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.funds.bankAccounts(variables.fundId),
        queryKeys.funds.detail(variables.fundId),
      ],
      successMessage: 'Bank account updated successfully',
    }
  );
};

export const useDeactivateBankAccount = () => {
  return useApiMutation<void, APIError, { fundId: string; accountId: string }>(
    ({ fundId, accountId }) =>
      fundService.deactivateBankAccount(fundId, accountId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.funds.bankAccounts(variables.fundId),
        queryKeys.funds.detail(variables.fundId),
      ],
      successMessage: 'Bank account deactivated successfully',
    }
  );
};

export const useCreateFundEntity = () => {
  return useApiMutation<
    FundEntity,
    APIError,
    {
      fundId: string;
      data: Omit<FundEntity, 'id' | 'fund_id' | 'created_at' | 'updated_at'>;
    }
  >(({ fundId, data }) => fundService.createFundEntity(fundId, data), {
    invalidateKeys: (_data, variables) => [
      queryKeys.funds.detail(variables.fundId),
    ],
    successMessage: 'Fund entity created successfully',
  });
};

export const useUpdateFundEntity = () => {
  return useApiMutation<
    FundEntity,
    APIError,
    {
      fundId: string;
      entityId: string;
      data: Partial<
        Omit<FundEntity, 'id' | 'fund_id' | 'created_at' | 'updated_at'>
      >;
    }
  >(
    ({ fundId, entityId, data }) =>
      fundService.updateFundEntity(fundId, entityId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.funds.detail(variables.fundId),
      ],
      successMessage: 'Fund entity updated successfully',
    }
  );
};

export const useDeleteFundEntity = () => {
  return useApiMutation<void, APIError, { fundId: string; entityId: string }>(
    ({ fundId, entityId }) => fundService.deleteFundEntity(fundId, entityId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.funds.detail(variables.fundId),
      ],
      successMessage: 'Fund entity deleted successfully',
    }
  );
};

export const useAddInvestorToFund = () => {
  return useApiMutation<
    void,
    APIError,
    { fundId: string; investorId: string; commitmentAmount: string }
  >(
    ({ fundId, investorId, commitmentAmount }) =>
      fundService.addInvestorToFund(fundId, investorId, commitmentAmount),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.funds.investors(variables.fundId),
        queryKeys.funds.detail(variables.fundId),
        queryKeys.funds.statistics(),
      ],
      successMessage: 'Investor added to fund successfully',
    }
  );
};

export const useEmailInvestors = () => {
  return useApiMutation<
    void,
    APIError,
    {
      fundId: string;
      data: EmailInvestorsRequest;
    }
  >(({ fundId, data }) => fundService.emailFundInvestors(fundId, data), {
    mutationKey: ['emailInvestors'],
    invalidateKeys: (_data, variables) => [
      queryKeys.funds.emails(variables.fundId),
    ],
    showSuccessToast: false,
  });
};

export const usePreviewEmail = () => {
  return useApiMutation<
    EmailPreviewResponse,
    APIError,
    {
      fundId: string;
    } & EmailPreviewRequest
  >(
    ({ fundId, investorId, contactId, templateId, subject, htmlContent }) =>
      fundService.previewFundEmail(
        fundId,
        investorId,
        contactId,
        templateId,
        subject,
        htmlContent
      ),
    {
      mutationKey: ['previewEmail'],
      showSuccessToast: false,
    }
  );
};

// Compound hooks for pages
export const useFundsPageData = () => {
  const queries = useQueries({
    queries: [
      {
        queryKey: queryKeys.funds.list(),
        queryFn: () => fundService.getFunds(),
        staleTime: 2 * 60 * 1000,
      },
      {
        queryKey: queryKeys.funds.statistics(),
        queryFn: () => fundService.getFundStatistics(),
        staleTime: 2 * 60 * 1000,
      },
    ],
  });

  const [fundsQuery, statisticsQuery] = queries;

  return {
    funds: fundsQuery.data ?? [],
    statistics: statisticsQuery.data,
    isLoading: fundsQuery.isLoading || statisticsQuery.isLoading,
    error: fundsQuery.error || statisticsQuery.error,
    refetch: () => {
      fundsQuery.refetch();
      statisticsQuery.refetch();
    },
  };
};

// The API's page size cap; one fund's companies fit, so the tab has no pager.
const FUND_COMPANIES_PAGE_SIZE = 500;

// Fund detail page data hook
export const useFundDetailPageData = (
  fundId: string,
  activeTab: string,
  companiesFinality: CapTableFinality
) => {
  const fundQuery = useFund(fundId);

  const fundInvestorsQuery = useInvestorsFundInvestors(fundId, {
    enabled: activeTab === 'investors',
  });

  const fundStatsQuery = useInvestorsFundStats(fundId);

  const portfolioCompaniesQuery = usePortfolioCompaniesHook({
    fund_id: fundId,
    page_size: FUND_COMPANIES_PAGE_SIZE,
    finality: companiesFinality,
  });

  return {
    fund: fundQuery.data,
    fundLoading: fundQuery.isLoading,
    fundError: fundQuery.error,
    fundInvestors: fundInvestorsQuery.data ?? [],
    fundStats: fundStatsQuery.data,
    portfolioCompanies: portfolioCompaniesQuery.data?.items ?? [],
    companiesLoading: portfolioCompaniesQuery.isLoading,
    companiesError: portfolioCompaniesQuery.error,
    refetchPortfolioCompanies: portfolioCompaniesQuery.refetch,
    isLoading:
      fundQuery.isLoading ||
      fundStatsQuery.isLoading ||
      portfolioCompaniesQuery.isLoading,
    error:
      fundQuery.error || fundStatsQuery.error || portfolioCompaniesQuery.error,
  };
};
